import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { User } from '../App';
import { MessageCircle, Plus, HelpCircle, Clock, Eye, ArrowLeft, Send, Trophy } from 'lucide-react';
import { Input } from './ui/input';
import { i18n } from '../utils/i18n';
import { WinnerCheckPopup } from './WinnerCheckPopup';

interface NewsItem {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  views: number;
  category: 'news' | 'announcement' | 'update';
}

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

interface ChatMessage {
  id: number;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface Winner {
  id: number;
  rank: number;
  referral_code: string;
  user_address: string;
  prize_amount: string;
  tokens_used: string;
}

interface WinnersByRank {
  rank: number;
  prize: number;
  count: number;
  winners: Winner[];
}

interface NewsSectionProps {
  user: User;
}

export function NewsSection({ user }: NewsSectionProps) {
  const [activeSection, setActiveSection] = useState<'news' | 'faq' | 'chat'>('news');
  const [selectedArticle, setSelectedArticle] = useState<NewsItem | null>(null);
  const [currentView, setCurrentView] = useState<'main' | 'article' | 'faq' | 'chat'>('main');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 1,
      type: 'assistant',
      content: `Hi ${user.nickname}! I'm Vatton AI. How can I help you today?`,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [winners, setWinners] = useState<WinnersByRank[]>([]);
  const [loadingWinners, setLoadingWinners] = useState(false);
  const [showWinnerCheckPopup, setShowWinnerCheckPopup] = useState(false);
  const [winnerInfo, setWinnerInfo] = useState<{
    rank: number | null;
    prizeAmount: number | null;
    hasParticipated: boolean;
  }>({
    rank: null,
    prizeAmount: null,
    hasParticipated: false
  });

  // Mock news data
  const newsItems: NewsItem[] = [
    {
      id: 1,
      title: 'Vatton Wallet Grand Opening Event',
      excerpt: 'Join our spectacular launch event with amazing rewards and exclusive benefits for early adopters.',
      content: `# Vatton Wallet Grand Opening Event

We're thrilled to announce the grand opening of Vatton Wallet, the next-generation cryptocurrency wallet designed for the modern digital economy.

## What's New?

- **Enhanced Security**: Multi-layer security with biometric authentication
- **DeFi Integration**: Seamless access to decentralized finance protocols  
- **Staking Rewards**: Earn up to 35% APY on your VTTN holdings
- **Cross-chain Support**: Support for multiple blockchain networks

## Launch Benefits

All early adopters who join during our launch week will receive:
- 100 VTTN welcome bonus
- Premium staking access
- Exclusive event participation rights
- Priority customer support

## How to Participate

1. Download the Vatton Wallet app
2. Complete registration with referral code
3. Verify your account
4. Start earning rewards immediately

Don't miss this opportunity to be part of the Vatton ecosystem from day one!`,
      author: 'Vatton Team',
      date: '2024-09-23',
      readTime: '3 min read',
      views: 1247,
      category: 'announcement'
    },
    {
      id: 2,
      title: 'New Staking Rewards Program Launch',
      excerpt: 'Introducing our revolutionary staking program with industry-leading APY rates and flexible terms.',
      content: `# New Staking Rewards Program

We're excited to introduce our new staking rewards program that offers some of the highest yields in the cryptocurrency space.

## Program Highlights

- **Basic Staking**: 15% APY with no lock-up period
- **Premium Staking**: 25% APY with 30-day lock-up
- **Validator Node**: 35% APY with professional node operation

## Getting Started

Staking with Vatton is simple and secure:

1. Navigate to the Staking section
2. Choose your preferred staking tier
3. Confirm your stake amount
4. Start earning rewards immediately

Your rewards are calculated daily and can be claimed at any time.`,
      author: 'DeFi Team',
      date: '2024-09-22',
      readTime: '2 min read',
      views: 891,
      category: 'update'
    },
    {
      id: 3,
      title: 'BOOM Event: The Ultimate Competition',
      excerpt: 'Get ready for the most exciting event in Vatton history with massive prizes and global participation.',
      content: `# BOOM Event: The Ultimate Competition

The first-ever BOOM Event is here! This is your chance to compete with players from around the world for incredible prizes.

## Event Details

- **Entry Fee**: 30 VTTN
- **Duration**: 48 hours of intense competition
- **Prize Pool**: Over 100,000 VTTN in total rewards
- **Participants**: Up to 2,000 competitors

## Prize Structure

- **1st Place**: 40,000 VTTN
- **2nd-11th Place**: 3,000 VTTN each
- **12th-50th Place**: 1,000 VTTN each
- **Multiple Winners**: Additional prizes for top performers

## Registration

Pre-registration is now open! Secure your spot early to take advantage of special bonuses and preparation time.

The competition will test your strategy, timing, and knowledge of the Vatton ecosystem. Are you ready to prove you're the ultimate Vatton champion?`,
      author: 'Events Team',
      date: '2024-09-21',
      readTime: '4 min read',
      views: 2156,
      category: 'news'
    }
  ];

  // Mock FAQ data
  const faqItems: FAQItem[] = [
    {
      id: 1,
      question: 'How to create an account?',
      answer: 'Open the Vatton Wallet app and follow the steps. Choose your nickname, set up a secure PIN, and follow the verification process. You can also use a referral code to get bonus rewards.',
      category: 'Getting Started'
    },
    {
      id: 2,
      question: 'How to add a payment method by this app?',
      answer: 'Go to the Wallet section and tap "Add Payment Method". You can connect your bank account, credit card, or other crypto wallets. All payment methods are secured with bank-level encryption.',
      category: 'Payment Methods'
    },
    {
      id: 3,
      question: 'What Time Does The Stock Market Open?',
      answer: 'The Vatton ecosystem operates 24/7, unlike traditional stock markets. However, some features like customer support follow business hours (9 AM - 6 PM UTC). Trading and staking rewards are processed continuously.',
      category: 'Trading'
    },
    {
      id: 4,
      question: 'Is The Stock Market Open On Weekends?',
      answer: 'Yes! Cryptocurrency markets and the Vatton ecosystem operate 24/7, including weekends and holidays. You can trade, stake, and participate in events any time.',
      category: 'Trading'
    }
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'news': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'announcement': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'update': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const handleArticleClick = (article: NewsItem) => {
    setSelectedArticle(article);
    setCurrentView('article');
  };

  const getAssistantResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();
    
    if (lowerMessage.includes('account') || lowerMessage.includes('create') || lowerMessage.includes('setup')) {
      return "To create an account: 1) Open Vatton Wallet app 2) Choose your nickname 3) Set up a secure PIN 4) Follow verification process. You can also use a referral code for bonus rewards!";
    }
    
    if (lowerMessage.includes('payment') || lowerMessage.includes('deposit') || lowerMessage.includes('withdraw')) {
      return "For payment methods: Go to Wallet → Add Payment Method. You can connect bank accounts, credit cards, or crypto wallets. All payments are secured with bank-level encryption.";
    }
    
    if (lowerMessage.includes('staking') || lowerMessage.includes('rewards') || lowerMessage.includes('apy')) {
      return "Staking options: Basic (15% APY), Premium (25% APY), Validator Node (35% APY). Navigate to Staking section, choose your tier, and start earning immediately!";
    }
    
    if (lowerMessage.includes('boom') || lowerMessage.includes('event') || lowerMessage.includes('competition')) {
      return "BOOM Event: Entry fee is 30 VTTN, runs for 48 hours. Prizes up to 40,000 VTTN! Pre-registration gives you early bird advantages. Check Events tab for details.";
    }
    
    if (lowerMessage.includes('referral') || lowerMessage.includes('friend') || lowerMessage.includes('invite')) {
      return "Referral program: Share your referral code with friends. You get 100 VTTN for each successful referral. Find your code in Settings or use the Share feature!";
    }
    
    if (lowerMessage.includes('security') || lowerMessage.includes('safe') || lowerMessage.includes('protect')) {
      return "Security features: Biometric authentication, PIN protection, multi-layer encryption. Enable 2FA in Settings for maximum security. Never share your PIN or seed phrase!";
    }
    
    if (lowerMessage.includes('balance') || lowerMessage.includes('wallet') || lowerMessage.includes('vttn')) {
      return `Your current balance is ${(user?.balance || 0).toFixed(2)} VTTN. You can earn more through staking, referrals, events, and airdrops. Check the Dashboard for detailed balance breakdown.`;
    }
    
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return "Hello! I'm Vatton AI, here to help you with any questions about Vatton Wallet. You can ask me about accounts, payments, staking, events, or anything else!";
    }
    
    if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
      return "You're welcome! Is there anything else I can help you with today?";
    }
    
    // Default response
    return "I understand you're asking about: " + userMessage + ". For detailed help, please check our FAQ section or feel free to ask me more specific questions about Vatton Wallet features!";
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(() => {
      const assistantResponse: ChatMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: getAssistantResponse(userMessage.content),
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickHelp = (topic: string) => {
    setInputValue(topic);
    handleSendMessage();
  };

  // 1차 붐이벤트 당첨자 목록 조회
  useEffect(() => {
    const fetchWinners = async () => {
      try {
        setLoadingWinners(true);
        const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://wallet.vatton.app';
        const response = await fetch(`${API_URL}/api/admin/boom-events/1/winners`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.winners) {
            // 모든 당첨자를 Winner 형식으로 변환
            const allWinners: Winner[] = data.winners.map((winner: any) => ({
              id: winner.id,
              rank: winner.rank,
              referral_code: winner.referral_code || '',
              user_address: winner.user_address || '',
              prize_amount: winner.prize_amount || '0',
              tokens_used: winner.tokens_used || '0'
            }));

            // 등수 범위별로 그룹화
            // 1등: rank 1 (1명)
            // 2등: rank 2~5 (4명)
            // 3등: rank 6~29 (24명)
            // 4등: rank 30~129 (100명)
            // 5등: rank 130~329 (200명)
            const rankGroups = [
              { displayRank: 1, minRank: 1, maxRank: 1, expectedCount: 1 },
              { displayRank: 2, minRank: 2, maxRank: 5, expectedCount: 4 },
              { displayRank: 3, minRank: 6, maxRank: 29, expectedCount: 24 },
              { displayRank: 4, minRank: 30, maxRank: 129, expectedCount: 100 },
              { displayRank: 5, minRank: 130, maxRank: 329, expectedCount: 200 }
            ];

            const winnersByRank: WinnersByRank[] = [];
            rankGroups.forEach(group => {
              const groupWinners = allWinners.filter(w => w.rank >= group.minRank && w.rank <= group.maxRank);
              if (groupWinners.length > 0) {
                // 그룹 내 첫 번째 당첨자의 상금을 사용 (소수점 제거)
                const prize = Math.round(parseFloat(groupWinners[0].prize_amount));
                winnersByRank.push({
                  rank: group.displayRank,
                  prize,
                  count: groupWinners.length,
                  winners: groupWinners
                });
              }
            });

            setWinners(winnersByRank);
          }
        }
      } catch (error) {
        console.error('당첨자 목록 조회 실패:', error);
      } finally {
        setLoadingWinners(false);
      }
    };

    fetchWinners();
  }, []);

  // 나의 당첨결과 조회
  const checkMyWinner = async () => {
    try {
      const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://wallet.vatton.app';
      const token = localStorage.getItem('vatton-jwt-token');
      
      if (!token) {
        console.error('토큰이 없습니다.');
        return;
      }

      const response = await fetch(`${API_URL}/api/boom/check-winner/1`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const inferredParticipation =
            typeof data.hasParticipated === 'boolean'
              ? data.hasParticipated
              : Boolean(data.rank ?? data.prizeAmount);

          setWinnerInfo({
            rank: data.rank ?? null,
            prizeAmount: data.prizeAmount ?? null,
            hasParticipated: inferredParticipation
          });
          setShowWinnerCheckPopup(true);
        }
      } else {
        console.error('당첨 결과 조회 실패');
      }
    } catch (error) {
      console.error('당첨 조회 오류:', error);
    }
  };

  const formatAddress = (address: string) => {
    if (!address) return 'N/A';
    return `${address.slice(0, 6)}...`;
  };

  const getRankColor = (rank: number) => {
    return 'bg-yellow-500 text-white';
  };

  // Article Detail View
  if (currentView === 'article' && selectedArticle) {
    return (
      <div className="h-full bg-background flex flex-col">
        {/* Header */}
        <div className="bg-background border-b border-border p-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setCurrentView('main')}
              className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            </button>
            <div className="flex-1">
              <h1 className="font-bold text-foreground">{selectedArticle.title}</h1>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedArticle.category)}`}>
                  {selectedArticle.category.toUpperCase()}
                </span>
                <div className="flex items-center text-muted-foreground text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {selectedArticle.readTime}
                </div>
                <div className="flex items-center text-muted-foreground text-xs">
                  <Eye className="w-3 h-3 mr-1" />
                  {selectedArticle.views}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          <div className="prose prose-sm max-w-none">
            {selectedArticle.content.split('\n').map((line, index) => {
              if (line.startsWith('# ')) {
                return <h1 key={index} className="text-2xl font-bold text-foreground mb-4 mt-6">{line.substring(2)}</h1>;
              } else if (line.startsWith('## ')) {
                return <h2 key={index} className="text-xl font-bold text-foreground mb-3 mt-5">{line.substring(3)}</h2>;
              } else if (line.startsWith('- ')) {
                return <li key={index} className="text-muted-foreground mb-1 ml-4">{line.substring(2)}</li>;
              } else if (line.match(/^\d+\./)) {
                return <li key={index} className="text-muted-foreground mb-1 ml-4 list-decimal">{line.substring(line.indexOf('.') + 2)}</li>;
              } else if (line.trim() === '') {
                return <br key={index} />;
              } else {
                return <p key={index} className="text-muted-foreground mb-3 leading-relaxed">{line}</p>;
              }
            })}
          </div>
        </div>
      </div>
    );
  }

  // FAQ Detail View
  if (currentView === 'faq') {
    return (
      <div className="h-full bg-background flex flex-col">
        {/* Header */}
        <div className="bg-background border-b border-border p-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setCurrentView('main')}
              className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            </button>
            <div>
              <h1 className="font-bold text-foreground">{i18n.t('faq')}</h1>
              <p className="text-muted-foreground text-sm">{i18n.t('frequentlyAskedQuestions')}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-foreground mb-2">{i18n.t('howCanWeHelp')}</h2>
            <p className="text-muted-foreground">{i18n.t('findAnswers')}</p>
          </div>

          {faqItems.map((item) => (
            <Card key={item.id} className="bg-card border-border">
              <CardContent className="p-4">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === item.id ? null : item.id)}
                  className="w-full text-left"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-foreground pr-4">{item.question}</h3>
                    <Plus
                      className={`w-5 h-5 text-muted-foreground transition-transform ${
                        expandedFAQ === item.id ? 'rotate-45' : ''
                      }`}
                    />
                  </div>
                </button>
                {expandedFAQ === item.id && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-muted-foreground text-sm leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Chat Detail View
  if (currentView === 'chat') {
    return (
      <div className="h-full bg-background flex flex-col">
        {/* Header */}
        <div className="bg-background border-b border-border p-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setCurrentView('main')}
              className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            </button>
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-foreground">Vatton AI</h1>
              <p className="text-green-600 text-sm">Online</p>
            </div>
          </div>
        </div>

        {/* Quick Help Topics */}
        <div className="bg-muted/50 p-4 border-b border-border">
          <h3 className="font-medium text-foreground mb-3 text-sm">Quick Help Topics</h3>
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={() => handleQuickHelp('How to create account')}
              className="bg-background rounded-lg p-3 text-left hover:bg-accent transition-colors text-xs"
            >
              Account Setup
            </button>
            <button 
              onClick={() => handleQuickHelp('How does staking work')}
              className="bg-background rounded-lg p-3 text-left hover:bg-accent transition-colors text-xs"
            >
              Staking Help
            </button>
            <button 
              onClick={() => handleQuickHelp('Payment method issues')}
              className="bg-background rounded-lg p-3 text-left hover:bg-accent transition-colors text-xs"
            >
              Payment Issues
            </button>
            <button 
              onClick={() => handleQuickHelp('Security settings')}
              className="bg-background rounded-lg p-3 text-left hover:bg-accent transition-colors text-xs"
            >
              Security
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex items-start space-x-3 ${
                message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                message.type === 'user' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
              }`}>
                {message.type === 'user' ? '👤' : '🤖'}
              </div>
              <div className={`flex-1 ${message.type === 'user' ? 'text-right' : ''}`}>
                <div className={`inline-block max-w-[80%] p-3 rounded-2xl text-sm ${
                  message.type === 'user'
                    ? 'bg-primary text-primary-foreground ml-auto'
                    : 'bg-muted text-foreground'
                }`}>
                  <p className="leading-relaxed">{message.content}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                🤖
              </div>
              <div className="bg-muted rounded-2xl p-3">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border">
          <div className="flex space-x-3">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Vatton AI anything..."
              className="flex-1 bg-muted border-none"
              disabled={isTyping}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isTyping}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-4"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main View
  return (
    <div className="h-full bg-background flex flex-col">
      {/* Section Navigation */}
      <div className="flex bg-muted mx-4 mb-4 rounded-xl p-1">
        <button
          onClick={() => setActiveSection('news')}
          className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
            activeSection === 'news'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <span className="text-sm font-medium">News</span>
        </button>
        <button
          onClick={() => setActiveSection('faq')}
          className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
            activeSection === 'faq'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <HelpCircle className="w-4 h-4" />
            <span className="text-sm font-medium">FAQ</span>
          </div>
        </button>
        <button
          onClick={() => setActiveSection('chat')}
          className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
            activeSection === 'chat'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <MessageCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Vatton AI</span>
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        {activeSection === 'news' && (
          <div className="space-y-4">
            {/* 나의 당첨결과 조회하기 버튼 (상단) */}
            <Button
              onClick={checkMyWinner}
              className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-xl shadow-lg"
            >
              <Trophy className="w-4 h-4 mr-2" />
              나의 당첨결과 조회하기
            </Button>

            {/* 1차 붐이벤트 당첨자 목록 */}
            {winners.length > 0 && (
              <Card className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    <h3 className="font-bold text-foreground text-lg">1차 붐이벤트 당첨자</h3>
                  </div>
                  
                  {loadingWinners ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
                      <p className="text-muted-foreground">당첨자 목록을 불러오는 중...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {winners.map((group) => (
                        <div key={group.rank} className="border-b border-border pb-4 last:border-b-0">
                          <div className={`mb-3 px-3 py-2 rounded-lg ${getRankColor(group.rank)}`}>
                            <p className="text-sm font-semibold">
                              {group.rank}등 상금: {group.prize.toLocaleString()} VTTN × {group.count}명 = 총 {Math.round(group.prize * group.count).toLocaleString()} VTTN
                            </p>
                          </div>
                          
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="text-left py-2 px-2 font-medium text-muted-foreground">등수</th>
                                  <th className="text-left py-2 px-2 font-medium text-muted-foreground">나의추천코드</th>
                                  <th className="text-left py-2 px-2 font-medium text-muted-foreground">지갑 주소</th>
                                  <th className="text-right py-2 px-2 font-medium text-muted-foreground">상금</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.winners.map((winner) => (
                                  <tr key={winner.id} className="border-b border-border/50 hover:bg-muted/50">
                                    <td className="py-2 px-2 text-foreground">{group.rank}등</td>
                                    <td className="py-2 px-2 font-mono text-foreground">{winner.referral_code || 'N/A'}</td>
                                    <td className="py-2 px-2 font-mono text-foreground text-xs">{formatAddress(winner.user_address)}</td>
                                    <td className="py-2 px-2 text-right font-medium text-green-600">{parseFloat(winner.prize_amount || '0').toFixed(0)} VTTN</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {newsItems.map((item) => (
              <Card
                key={item.id}
                className="bg-card border-border hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleArticleClick(item)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(item.category)}`}>
                          {item.category.toUpperCase()}
                        </span>
                        <div className="flex items-center text-muted-foreground text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          {item.readTime}
                        </div>
                        <div className="flex items-center text-muted-foreground text-xs">
                          <Eye className="w-3 h-3 mr-1" />
                          {item.views}
                        </div>
                      </div>
                      <h3 className="font-bold text-foreground mb-1 line-clamp-1">{item.title}</h3>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-2">{item.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{item.author}</span>
                        <span>{new Date(item.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* 나의 당첨결과 조회하기 버튼 (하단) */}
            <Button
              onClick={checkMyWinner}
              className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-xl shadow-lg"
            >
              <Trophy className="w-4 h-4 mr-2" />
              나의 당첨결과 조회하기
            </Button>
          </div>
        )}

        {activeSection === 'faq' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <HelpCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Frequently Asked Questions</h2>
            <p className="text-muted-foreground mb-6">Get answers to common questions</p>

            <Button
              onClick={() => setCurrentView('faq')}
              className="w-full h-16 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg rounded-2xl"
            >
              <HelpCircle className="w-6 h-6 mr-3" />
              View FAQ
            </Button>
          </div>
        )}

        {activeSection === 'chat' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Vatton AI Support</h2>
            <p className="text-muted-foreground mb-6">Get help from our AI assistant</p>

            <Button
              onClick={() => setCurrentView('chat')}
              className="w-full h-16 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-bold text-lg rounded-2xl"
            >
              <MessageCircle className="w-6 h-6 mr-3" />
              Start Chat with Vatton AI
            </Button>
          </div>
        )}
      </div>

      {/* Winner Check Popup */}
      <WinnerCheckPopup
        isOpen={showWinnerCheckPopup}
        onClose={() => setShowWinnerCheckPopup(false)}
        nickname={user?.nickname || ''}
        rank={winnerInfo.rank}
        prizeAmount={winnerInfo.prizeAmount}
        hasParticipated={winnerInfo.hasParticipated}
        onViewAllWinners={() => {
          // 이미 뉴스 탭에 있으므로 스크롤만 이동
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onParticipate={() => {
          // 2차 붐이벤트 참여하기 - Events 탭으로 이동
          // hash를 제거하여 Events 탭이 활성화되도록 함
          if (window.location.hash === '#news') {
            window.location.hash = '';
          }
          // Events 탭 버튼 찾기 및 클릭
          setTimeout(() => {
            const eventsTab = Array.from(document.querySelectorAll('button')).find(btn => {
              const text = btn.textContent || '';
              return text.includes('Events') && !text.includes('News');
            });
            if (eventsTab) {
              (eventsTab as HTMLElement).click();
            }
          }, 100);
        }}
      />
    </div>
  );
}